import { CheckCircle, Eye, Heart, HourglassSimple, Microphone, Prohibit, SealCheck, ShieldCheck, Star, Tag } from "@phosphor-icons/react";
import { useSeo, useSite } from "@/hooks/useSite";
import { Face } from "@/lib/brand";
import WaitlistForm from "@/components/site/WaitlistForm";
import { Band, Chip, GlassIcon, Heading, IconTile, SitePage, StoreButtons } from "@/components/site/SiteBits";
import { CrushesScreen, DiscoverScreen, Phone, ProfileScreen } from "@/components/site/Phones";

const pick = (f, prefix, n) => Array.from({ length: n }, (_, i) => ({ title: f[`${prefix}_${i + 1}_title`], line: f[`${prefix}_${i + 1}_line`] || f[`${prefix}_${i + 1}_body`] })).filter((x) => x.title);

function Hero({ f, hero, stores }) {
  return (
    <div className="pt-4 lg:pr-8 lg:pt-10" data-testid="home-hero-text">
      <Chip testId="home-hero-chip">{f.hero_chip}</Chip>
      <Heading as="h1" title={hero.headline} accent={hero.accent} stack className="s-h1 mt-6 lg:!text-[2.85rem] min-[1200px]:!text-[3.35rem]" testId="home-headline" />
      {f.lead && <p className="s-muted mt-6 max-w-xl text-lg leading-8" data-testid="home-lead">{f.lead}</p>}
      <div className="mt-7 flex flex-wrap gap-3" data-testid="home-trust">
        {f.trust_1 && <span className="s-pill"><CheckCircle size={18} weight="fill" className="s-coral" />{f.trust_1}</span>}
        {f.trust_2 && <span className="s-pill"><Heart size={18} weight="fill" className="s-coral" />{f.trust_2}</span>}
      </div>
      <StoreButtons stores={stores} className="mt-8" testId="home-stores" />
    </div>
  );
}

// Middle zone (≥1024px): scaled, tilted Discover phone that runs behind the form card's left edge; Maya card over its left edge.
function HeroPhone({ f }) {
  return (
    <div className="relative z-0 hidden h-[391px] w-[189px] justify-self-end lg:-mr-[64px] lg:mt-2 lg:block min-[1200px]:h-[458px] min-[1200px]:w-[221px]" data-testid="home-hero-phone">
      <div className="origin-top-left scale-[0.7] min-[1200px]:scale-[0.82]"><div className="rotate-[9deg]"><Phone testId="home-discover-phone"><DiscoverScreen /></Phone></div></div>
      {f.overlap_title && (
        <div className="absolute -left-[72px] bottom-[18%] z-20 flex w-max max-w-[14.5rem] items-center gap-3 rounded-[18px] bg-[#FFFFFF] px-4 py-3 text-[#1C1412] shadow-[0_20px_40px_-16px_rgba(40,15,8,0.45)]" data-testid="home-overlap-card">
          <Face name="wink" className="h-10 w-10" /><span><b className="block text-sm">{f.overlap_title}</b><span className="block text-xs text-[#75645E]">{f.overlap_line}</span></span>
        </div>)}
    </div>
  );
}

function WaitlistCard({ f }) {
  return (
    <div id="waitlist" className="s-card relative z-10 scroll-mt-24 p-6 sm:p-9 lg:sticky lg:top-24 lg:mt-12" data-testid="home-waitlist-card">
      <div className="flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white" style={{ background: "var(--s-grad)" }} aria-hidden><Star size={24} weight="fill" /></span>
        <div><h2 className="text-2xl font-bold tracking-tight" data-testid="home-waitlist-title">{f.waitlist_title}</h2>
          {f.waitlist_line && <p className="s-muted mt-1 text-sm leading-6" data-testid="home-waitlist-line">{f.waitlist_line}</p>}</div>
      </div>
      <div className="mt-7"><WaitlistForm submitLabel={f.cta} footnote={f.waitlist_fine} /></div>
    </div>
  );
}

function Steps({ f }) {
  return (
    <section className="mt-28" aria-labelledby="home-how" data-testid="home-how">
      <p className="s-eyebrow">{f.how_eyebrow}</p>
      <Heading id="home-how" title={f.how_title} accent={f.how_accent} className="s-h2 mt-3" />
      <ol className="mt-10 grid gap-5 md:grid-cols-3">
        {pick(f, "step", 3).map((s, i) => (
          <li key={i} className="s-card p-7" data-testid={`home-step-${i + 1}`}>
            <div className="flex items-center justify-between"><Face name={["crush", "wink", "super_crush"][i]} className="h-12 w-12" /><span className="s-coral text-sm font-bold tabular-nums">0{i + 1}</span></div>
            <h3 className="mt-6 text-xl font-bold">{s.title}</h3><p className="s-muted mt-2 leading-7">{s.line}</p>
          </li>))}
      </ol>
    </section>
  );
}

function Point({ icon, title, line, testId }) {
  return <li className="flex gap-4" data-testid={testId}><IconTile icon={icon} /><div><h3 className="font-bold">{title}</h3><p className="s-muted mt-1 text-[0.9375rem] leading-6">{line}</p></div></li>;
}

function Glass({ f }) {
  return (
    <section className="mt-28 grid items-center gap-14 lg:grid-cols-[auto_1fr]" aria-labelledby="home-glass" data-testid="home-glass">
      <div className="hidden justify-center lg:flex"><Phone tilt="s-tilt-left" testId="home-profile-phone"><ProfileScreen copy={f} /></Phone></div>
      <div>
        <p className="s-eyebrow inline-flex items-center gap-2"><GlassIcon size={16} />{f.glass_eyebrow}</p>
        <Heading id="home-glass" title={f.glass_title} accent={f.glass_accent} className="s-h2 mt-3" />
        {f.glass_lead && <p className="s-muted mt-5 max-w-xl text-lg leading-8">{f.glass_lead}</p>}
        <ul className="mt-9 space-y-6">{pick(f, "glass", 3).map((p, i) => <Point key={i} icon={[Microphone, Heart, ShieldCheck][i]} {...p} testId={`home-glass-point-${i + 1}`} />)}</ul>
      </div>
    </section>
  );
}

const WHY_ICONS = [Eye, Prohibit, (p) => <GlassIcon size={22} {...p} />, SealCheck, HourglassSimple, Tag];
function Why({ f }) {
  return (
    <section className="mt-28" aria-labelledby="home-why" data-testid="home-why">
      <p className="s-eyebrow">{f.why_eyebrow}</p>
      <Heading id="home-why" title={f.why_title} accent={f.why_accent} className="s-h2 mt-3" />
      <div className="mt-12 grid items-center gap-14 xl:grid-cols-[1fr_auto]">
        <ul className="grid gap-x-8 gap-y-9 sm:grid-cols-2 lg:grid-cols-3">{pick(f, "why", 6).map((p, i) => <Point key={i} icon={WHY_ICONS[i]} {...p} testId={`home-why-${i + 1}`} />)}</ul>
        <div className="hidden justify-center xl:flex"><Phone tilt="s-tilt" testId="home-crushes-phone"><CrushesScreen /></Phone></div>
      </div>
    </section>
  );
}

export default function Home() {
  const { data, error, loading, reload } = useSite();
  const f = data?.docs?.home_copy?.fields || {};
  const hero = data?.hero || {};
  useSeo([hero.headline, hero.accent].filter(Boolean).join(" ") || "Crushwink", f.lead);
  return (
    <SitePage loading={loading} error={error} onRetry={reload} testId="home-page">
      <section className="grid items-start gap-12 lg:grid-cols-[minmax(0,1fr)_150px_400px] lg:gap-x-6 min-[1200px]:grid-cols-[minmax(0,1fr)_180px_440px]" data-testid="home-hero">
        <Hero f={f} hero={hero} stores={data?.stores} />
        <HeroPhone f={f} />
        <WaitlistCard f={f} />
      </section>
      <Steps f={f} />
      <Glass f={f} />
      <Why f={f} />
      <Band title={f.band_title} accent={f.band_accent} line={f.band_line} button={f.band_button} testId="home-band" />
    </SitePage>
  );
}
